import { DetailSectionTemplateWithTranslation } from "components/MainSectionTemplate/translation/DetailSectionTemplateWithTranslation";
import { RenderEditOptions, Section } from "components/MainSectionTemplate/types";
import { BasicTemplatePill } from "components/Pill/BasicTemplatePill";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import RequiredTextInputWithMergeField from "components/ValidatedFields/RequiredTextInputWithMergeField";
import { HtmlEditor } from "components/ValidatedFields/HtmlEditor";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import React from "react";
import { nounToLower } from "utilities/translationUtils";
import { useIsSystemOneAccount } from "common/SessionContext";

interface Props {
  title: string
  emailTemplate: server.dto.MessageTemplate
  mergeFieldTopicLabel: string
  availableMergeFieldTopics: server.dto.AvailableMergeFieldTopic[]
}

interface EmailTemplateGeneralState {
  title: string
  expectAttachment: boolean
  basicTemplate: boolean
}

export const EmailTemplateDetailSection = (props: Props) => {
  const isSystemOneAccount = useIsSystemOneAccount();
  const [generalDetailsState, setGeneralDetailsState] = React.useState<EmailTemplateGeneralState>({
    title: props.emailTemplate.title,
    expectAttachment: props.emailTemplate.expectAttachment,
    basicTemplate: props.emailTemplate.basicTemplate,
  });

  const topSection: Section = {
    title: props.title,
    type: "regular",
    key: "generalSection",
    fields: [{
      key: "title",
      label: RESX.GeneralLabels.Name,
      render: () => <div>{generalDetailsState.title}</div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInput
          validatorKey="title"
          focusOnMount
          maxLength={serverReference.constants.messageTemplate.maxTitleLength}
          value={value}
          onChange={(name) => { onChange(name) }}
          onEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: true,
        type: "confirmOrCancelButton",
        initialValue: generalDetailsState.title,
        submitChange: (title: string) => window.CoreApi.MessageTemplate.UpdateDetails({
          expectAttachment: generalDetailsState.expectAttachment,
          basicTemplate: generalDetailsState.basicTemplate,
          messageTemplateId: props.emailTemplate.messageTemplateId,
          title,
          preferredTemplate: props.emailTemplate.preferredTemplate,
        }),
      },
    }, {
      key: "expectAttachment",
      label: RESX.File.ExpectAttachment,
      render: () => <YesNoText value={generalDetailsState.expectAttachment} />,
      inPageEdit: {
        render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
          value={value}
          onChange={(v) => onChange(v, true)}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: generalDetailsState.expectAttachment,
        submitChange: (expectAttachment: boolean) => window.CoreApi.MessageTemplate.UpdateDetails({
          expectAttachment,
          basicTemplate: generalDetailsState.basicTemplate,
          messageTemplateId: props.emailTemplate.messageTemplateId,
          title: generalDetailsState.title,
          preferredTemplate: props.emailTemplate.preferredTemplate,
        }),
      }
    }, {
      key: "basicTemplate",
      label: RESX.Advancing.ReadOnly,
      hidden: !isSystemOneAccount && !generalDetailsState.basicTemplate,
      editDisabled: !isSystemOneAccount,
      render: () => <YesNoText value={generalDetailsState.basicTemplate} yesLabel={<BasicTemplatePill />} />,
      inPageEdit: {
        render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
          value={value}
          onChange={(v) => onChange(v, true)}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: generalDetailsState.basicTemplate,
        submitChange: (basicTemplate: boolean) => window.CoreApi.MessageTemplate.UpdateDetails({
          expectAttachment: generalDetailsState.expectAttachment,
          basicTemplate,
          messageTemplateId: props.emailTemplate.messageTemplateId,
          title: generalDetailsState.title,
          preferredTemplate: props.emailTemplate.preferredTemplate,
        }),
      }
    }],
  }

  const onValueChanged = (key: string, value: any) => {
    setGeneralDetailsState(prev => ({
      ...prev,
      [key]: value
    }));
  }

  const isInPageEditable = (props.emailTemplate.permissions.canModify && !generalDetailsState.basicTemplate) || isSystemOneAccount;

  return <DetailSectionTemplateWithTranslation
    queryKey={`emailTemplate-${props.emailTemplate.messageTemplateId}`}
    isInPageEditable={isInPageEditable}
    topSection={topSection}
    listApi={() => window.CoreApi.MessageTemplate.ListEmailTranslation(props.emailTemplate.messageTemplateId)}
    addTranslationApi={(language) => window.CoreApi.MessageTemplate.CreateEmailTranslation({
      messageTemplateId: props.emailTemplate.messageTemplateId,
      language,
    })}
    removeTranslationApi={(language) => window.CoreApi.MessageTemplate.DeleteTranslation({
      messageTemplateId: props.emailTemplate.messageTemplateId,
      language,
    })}
    renderFieldsForTranslation={(language, formStateForTranslation) => [{
      key: "subject",
      label: RESX.Message.Subject,
      render: () => <div>{formStateForTranslation.subject}</div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInputWithMergeField
          validatorKey="subject"
          focusOnMount
          maxLength={serverReference.constants.messageTemplate.maxTranslationSubjectLength}
          value={value}
          onChange={(subject) => { onChange(subject); }}
          onEnter={() => submit()}
          onEsc={() => cancel()}
          sectionName={nounToLower(props.mergeFieldTopicLabel)}
          availableMergeFieldTopics={props.availableMergeFieldTopics}
        />,
        fullWidth: true,
        type: "confirmOrCancelButton",
        initialValue: formStateForTranslation.subject,
        submitChange: (subject: string) => window.CoreApi.MessageTemplate.SetEmailTranslation({
          messageTemplateId: props.emailTemplate.messageTemplateId,
          subject,
          body: formStateForTranslation.body,
          language,
        }),
      },
    }, {
      key: "body",
      label: RESX.Message.Body,
      isEmpty: formStateForTranslation.body == null || formStateForTranslation.body === "",
      render: () => <div dangerouslySetInnerHTML={{
        __html: formStateForTranslation.body
      }}></div>,
      inPageEdit: {
        render: ({ value, onChange, cancel }: RenderEditOptions<string>) => <HtmlEditor
          toolbar="InsertMergefield"
          content={value}
          maxContentLength={serverReference.constants.messageTemplate.maxTranslationBodyLength}
          mergeFields={{
            sectionName: nounToLower(props.mergeFieldTopicLabel),
            availableMergeFieldTopics: props.availableMergeFieldTopics,
          }}
          focusOnMount
          onChange={(newValue: string) => onChange(newValue)}
          onCtrlEnter={(newValue) => onChange(newValue, true)}
          onEsc={() => cancel()}
        />,
        disableClickOutside: true,
        fullWidth: true,
        type: "confirmOrCancelButton",
        initialValue: formStateForTranslation.body,
        submitChange: (body: string) => window.CoreApi.MessageTemplate.SetEmailTranslation({
          messageTemplateId: props.emailTemplate.messageTemplateId,
          subject: formStateForTranslation.subject,
          body,
          language,
        }),
      },
    }]}
    onValueChanged={onValueChanged}
  />
}
